import React, { useEffect, useReducer, useRef, useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import { ReactComponent as PlusIcon } from "../../assets/icons/plusIcon.svg";
import { useOutletContext } from "react-router-dom";
import cloneDeep from "clone-deep";

import CustomModal from "../Generic/CustomModal";
import SecondaryButton from "../Generic/SecondaryButton";
import EmailRolePair from "../Generic/EmailRolePair";
import { isEmail } from "validator";
import { useMembersContext } from "../../context/useMembersContext";
import { useProjectContext } from "../../context/useProjectContext";
import { useLicensingContext } from "../../context/useLicensingContext";
import { showGenericError, showSuccessNotification, showInfoNotification } from "../../helper/Notifications";

const InviteMembersModal = ({ setIsModalOpen, onInvited }) => {
    const { inviteMembers } = useMembersContext();
    const { selectedProject } = useProjectContext();
    const { setLoading } = useOutletContext();
    const { subscription, platformProducts } = useLicensingContext();

    const availableProducts = platformProducts.map(p => ({
        ...p,
        subscribed: subscription?.items?.some(
            item => item.product_slug === p.slug
        ) ?? false
    }));

    const [emailRolePair, setEmailRolePail] = useState([
        {
            email: "",
            role: { value: null, label: "Role" },
            product_slugs: []
        }
    ]);

    const handleAddKeyValuePair = () => {
        let newEmailRolePair = {
            email: "",
            role: { value: null, label: "Role" },
            product_slugs: []
        };
        setEmailRolePail([...emailRolePair, newEmailRolePair]);
    };

    const handleChangeEmailRolePair = (index, pair) => {
        let clone = cloneDeep(emailRolePair);
        clone[index] = pair;
        setEmailRolePail(clone);
    };

    const handleRemovePair = (index) => {
        let clone = cloneDeep(emailRolePair);
        clone.splice(index, 1);
        setEmailRolePail(clone);
    };

    const handleInvite = () => {
        setLoading(true);
        let members = emailRolePair
            .filter(item => item.email.trim() !== '' && item.role.value !== null)
            .map((item) => ({
                email: item.email,
                role: item.role.value,
                ...(item.role.value === "member" && {
                    product_slugs: item.product_slugs || []
                })
            }));

        return new Promise((resolve, reject) => {
            inviteMembers(
                selectedProject.id,
                members,
                (response) => {
                    onInvited?.();
                    setLoading(false);

                    const results = response.data?.results || [];
                    const addedEmails = results.filter((r) => r.status === "invited" || r.status === "added").map((r) => r.email);
                    const existingEmails = results.filter((r) => r.status === "already_member").map((r) => r.email);

                    if (addedEmails.length > 0) {
                        showSuccessNotification(
                            `${addedEmails.length === 1 ? "Member" : "Members"} added successfully`
                        );
                    }
                    if (existingEmails.length > 0) {
                        const isSingle = existingEmails.length === 1;
                        showInfoNotification(
                            `${existingEmails.join(", ")} ${isSingle ? "is already a member" : "are already members"} of this project - use the menu next to ${isSingle ? "their name" : "their names"} to change their access.`
                        );
                    }

                    resolve(response);
                },
                (error) => {
                    showGenericError();
                    setLoading(false);
                    reject(error);
                }
            )
        })
    }

    const checkValid = (list) => {
        const filledRows = list.filter(
            element => element.email.trim() !== '' || element.role.value !== null
        );

        if (filledRows.length === 0) return false;

        // All filled rows must be valid
        let valid = true;
        filledRows.forEach(element => {
            if (!isEmail(element.email)) {
                valid = false;
                return;
            }
            if (element.role.value == null) {
                valid = false;
                return;
            }
            if (element.role.value === "member" && (!element.product_slugs || element.product_slugs.length === 0)) {
                valid = false;
            }
        });
        return valid;
    }

    const handleClose = () => {
        setIsModalOpen(false);
        // Reset form
        setEmailRolePail([{
            email: "",
            role: { value: null, label: "Role" },
            product_slugs: []
        }]);
    };


    return (
        <CustomModal
            isOpen={true}
            onOpenChange={handleClose}
            iconType="info"
            iconAlignment="left"
            title="Add members"
            titleAlignment="left"
            buttonLayout="column"
            showCloseButton={true}
            autoCloseOnSuccess={true}
            width={500}
            buttons={[
                {
                    text: "Add members",
                    action: handleInvite,
                    variant: "primary",
                    disabled: !checkValid(emailRolePair)
                }
            ]}
        >
            <ModalContent>
                <SectionTitle>Team Members</SectionTitle>
                <MembersList>
                    {emailRolePair.map((pair, index) => (
                        <EmailRolePair
                            key={index}
                            pair={pair}
                            index={index}
                            handleChangeEmailRolePair={handleChangeEmailRolePair}
                            handleRemove={handleRemovePair}
                            availableProducts={availableProducts}
                        />
                    ))}
                </MembersList>

                <AddRowButton>
                    <SecondaryButton
                        style={{
                            border: "none",
                            padding: "8px 12px",
                            color: lightThemeColors.primaryColor,
                            background: "transparent"
                        }}
                        icon={<PlusIcon style={{ width: "16px", height: "16px" }} />}
                        text={"Add another member"}
                        onClick={handleAddKeyValuePair}
                    />
                </AddRowButton>
            </ModalContent>
        </CustomModal>
    );
};

export default InviteMembersModal;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SectionTitle = styled.h3`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin: 0;
`;

const MembersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

const AddRowButton = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-top: 8px;
`;